import { CHAT_AGENT_DEFAULT_MAX_STEPS, type ChatAgentSettings } from '@sweetistics/contracts/chat';
import { useCallback, useRef, useState } from 'react';
import type { ChatRequest } from '@/app/api/chat/types';
import { buildMessagesPayload, extractModelAnnouncement } from '@/components/chat/chat-helpers';
import type { Message } from '@/components/chat/chat-types';
import { buildCostSummary, buildUsageSummary } from '@/lib/ai/costs';
import { hasUsageMetrics, mergeUsageMetrics, normalizeUsageMetrics, type UsageMetrics } from '@/lib/ai/usage-metrics';
import { isNonEmptyString, isPlainObject } from '@/lib/utils/type-guards';
import { scheduleInitialScroll } from './helpers';
import type {
  StreamingState,
  StreamingUIEvent,
  SubmitExistingFn as SubmitExistingFunction,
  SubmitFn as SubmitFunction,
  UseChatStreamingOptions,
  UseChatStreamingResult,
} from './types';
import { useStreamingErrors } from './useStreamingErrors';
import { useStreamingSubscription } from './useStreamingSubscription';
import { useStreamingTokens } from './useStreamingTokens';

const DEFAULT_AGENT_REQUEST: ChatAgentSettings = Object.freeze({ maxSteps: CHAT_AGENT_DEFAULT_MAX_STEPS });

class ChatApiResponseError extends Error {
  status: number;
  statusText: string;
  code: string | null;
  body: string;
  path: string;

  constructor(parameters: {
    message: string;
    status: number;
    statusText: string;
    code: string | null;
    body: string;
    path: string;
  }) {
    super(parameters.message);
    this.name = 'ChatApiResponseError';
    this.status = parameters.status;
    this.statusText = parameters.statusText;
    this.code = parameters.code;
    this.body = parameters.body;
    this.path = parameters.path;
  }
}

type ServerErrorDetails = {
  message: string | null;
  code: string | null;
};

const extractServerErrorDetails = (raw: string): ServerErrorDetails => {
  if (!raw) {
    return { message: null, code: null };
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (isPlainObject(parsed)) {
      const { error, message, code } = parsed as { error?: unknown; message?: unknown; code?: unknown };
      let resolvedMessage: string | null = null;
      if (isNonEmptyString(error)) {
        resolvedMessage = error.trim();
      } else if (isNonEmptyString(message)) {
        resolvedMessage = message.trim();
      }
      const resolvedCode = isNonEmptyString(code) ? code.trim() : null;
      if (resolvedMessage) {
        return { message: resolvedMessage, code: resolvedCode };
      }
      return { message: null, code: resolvedCode };
    }
  } catch {
    // Ignore JSON parse failures and fall back to plain text handling below.
  }

  const trimmed = raw.trim();
  if (!trimmed || trimmed === '{}' || trimmed === '[]') {
    return { message: null, code: null };
  }
  return { message: trimmed, code: null };
};

const CHAT_URL_PATTERN = /^https?:\/\//i;

const normalizePostPath = (path?: string): string => {
  const trimmed = path?.trim();
  if (!trimmed) {
    return '/api/chat';
  }
  if (CHAT_URL_PATTERN.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('/')) {
    return trimmed;
  }
  throw new Error(`Chat postPath must be an absolute path or URL. Received "${trimmed}".`);
};

export const useChatStreaming = ({
  log,
  messagesRef,
  setMessages,
  selectedModelId,
  clearInput,
  scrollToBottom,
  onError,
  postPath,
}: UseChatStreamingOptions): UseChatStreamingResult => {
  const resolvedPostPath = normalizePostPath(postPath);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [streamingState, setStreamingState] = useState<StreamingState>({ assistantId: null, started: false });
  const [latestUIEvent, setLatestUIEvent] = useState<StreamingUIEvent | null>(null);
  const streamingStateReference = useRef(streamingState);
  const abortControllerReference = useRef<AbortController | null>(null);
  const latestAssistantIdReference = useRef<string | null>(null);
  const assistantUsageReference = useRef<Map<string, UsageMetrics>>(new Map());
  const assistantModelReference = useRef<Map<string, string | null>>(new Map());
  const messageIdSequenceReference = useRef(0);

  const updateStreamingState = useCallback((state: StreamingState) => {
    streamingStateReference.current = state;
    setStreamingState(state);
  }, []);

  const errorHandlers = useStreamingErrors({ log, setMessages });

  const generateMessageId = useCallback((prefix: 'assistant' | 'user') => {
    messageIdSequenceReference.current += 1;
    const sequence = messageIdSequenceReference.current;
    return `${prefix}-${Date.now()}-${sequence}`;
  }, []);

  const updateAssistantTelemetry = useCallback(
    (assistantId: string, delta: UsageMetrics) => {
      if (assistantId === undefined) {
        return;
      }

      const existing = assistantUsageReference.current.get(assistantId);
      const merged = existing ? mergeUsageMetrics(existing, delta) : { ...delta };
      assistantUsageReference.current.set(assistantId, merged);

      if (!hasUsageMetrics(merged)) {
        return;
      }

      const normalized = normalizeUsageMetrics(merged);
      const usageSummary = buildUsageSummary({
        promptTokens: normalized.promptTokens,
        completionTokens: normalized.completionTokens,
        totalTokens: normalized.totalTokens,
        reasoningTokens: normalized.reasoningTokens ?? null,
      });

      const usageReasoning = usageSummary.reasoningTokens ?? 0;
      const hasUsageData = hasUsageMetrics(merged);

      const canonicalModelId = assistantModelReference.current.get(assistantId);
      const costSummary = canonicalModelId
        ? buildCostSummary(canonicalModelId, usageSummary.promptTokens, usageSummary.completionTokens)
        : null;

      setMessages((previous) =>
        previous.map((message) => {
          if (message.id !== assistantId) {
            return message;
          }
          const previousTelemetry = message.telemetry ?? {};
          let changed = false;
          const nextTelemetry = { ...previousTelemetry };

          if (hasUsageData) {
            const existingUsage = previousTelemetry.usage;
            if (
              !existingUsage ||
              existingUsage.promptTokens !== usageSummary.promptTokens ||
              existingUsage.completionTokens !== usageSummary.completionTokens ||
              existingUsage.totalTokens !== usageSummary.totalTokens ||
              (existingUsage.reasoningTokens ?? 0) !== usageReasoning
            ) {
              nextTelemetry.usage = usageSummary;
              changed = true;
            }
          }

          if (costSummary) {
            const existingCost = previousTelemetry.cost;
            if (
              !existingCost ||
              existingCost.inputCostCents !== costSummary.inputCostCents ||
              existingCost.outputCostCents !== costSummary.outputCostCents ||
              existingCost.totalCostCents !== costSummary.totalCostCents ||
              existingCost.pricingModel !== costSummary.pricingModel
            ) {
              nextTelemetry.cost = costSummary;
              changed = true;
            }
          }

          if (!changed) {
            return message;
          }

          return {
            ...message,
            telemetry: nextTelemetry,
          };
        })
      );
    },
    [setMessages]
  );

  const { processStreamLine, resetPendingAssistantText } = useStreamingTokens({
    log,
    setMessages,
    streamingStateRef: streamingStateReference,
    updateStreamingState,
    errorHandlers: { handleStreamError: errorHandlers.handleStreamError },
    onUsage: updateAssistantTelemetry,
    onUiEvent: setLatestUIEvent,
  });

  const { readStream, postChat } = useStreamingSubscription({
    log,
    messagesRef,
    setMessages,
    processStreamLine,
    handlers: {
      onStreamComplete: ({ assistantId, stats }) => {
        setIsSyncing(false);
        updateStreamingState({ assistantId: null, started: false });
        if (latestAssistantIdReference.current === assistantId) {
          latestAssistantIdReference.current = null;
        }
        assistantUsageReference.current.delete(assistantId);
        assistantModelReference.current.delete(assistantId);
        log.info('Stream complete', stats);
      },
    },
    postPath: resolvedPostPath,
  });

  const runStream = useCallback(
    async (assistantMessageId: string, payload: ChatRequest) => {
      abortControllerReference.current = new AbortController();
      const signal = abortControllerReference.current.signal;
      setLatestUIEvent(null);
      try {
        const response = await postChat(payload, signal);

        if (!response.ok) {
          const raw = await response.text();
          const { message: serverMessage, code: serverCode } = extractServerErrorDetails(raw);
          const statusSuffix = ` (HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''})`;
          const normalizedServerMessage = typeof serverMessage === 'string' ? serverMessage : '';
          const friendlyMessage =
            normalizedServerMessage.trim().length > 0
              ? `${normalizedServerMessage}${statusSuffix}`
              : `Chat request failed${statusSuffix}`;

          console.error('Chat API error response:', {
            status: response.status,
            statusText: response.statusText,
            code: serverCode ?? null,
            message: serverMessage ?? null,
            bodyPreview: raw.slice(0, 200) || null,
            path: resolvedPostPath,
          });
          log.error('Chat API error response', {
            status: response.status,
            statusText: response.statusText,
            code: serverCode ?? null,
            message: serverMessage ?? null,
            bodyPreview: raw.slice(0, 200),
            path: resolvedPostPath,
          });

          throw new ChatApiResponseError({
            message: friendlyMessage,
            status: response.status,
            statusText: response.statusText,
            code: serverCode ?? null,
            body: raw,
            path: resolvedPostPath,
          });
        }

        const modelAnnouncement = extractModelAnnouncement(response);
        log.info('Response received', {
          status: response.status,
          contentType: response.headers.get('content-type') || 'unknown',
          providerModelId: modelAnnouncement?.providerModelId ?? null,
          canonicalModelId: modelAnnouncement?.canonicalModelId ?? null,
          provider: modelAnnouncement?.provider ?? null,
          selectedModelId,
        });
        assistantModelReference.current.set(
          assistantMessageId,
          modelAnnouncement?.canonicalModelId ?? modelAnnouncement?.providerModelId ?? null
        );

        await readStream(response, assistantMessageId);
      } finally {
        resetPendingAssistantText(assistantMessageId);
      }
    },
    [log, postChat, readStream, resetPendingAssistantText, resolvedPostPath, selectedModelId]
  );

  const submitUserMessage = useCallback<SubmitExistingFunction>(
    async (messageId: string) => {
      if (messageId === undefined || isLoading) {
        return false;
      }

      const history = messagesRef.current;
      const messageIndex = history.findIndex((message) => message.id === messageId);
      if (messageIndex === -1) {
        return false;
      }

      const userMessage = history.find((_, index) => index === messageIndex);
      if (!userMessage || userMessage.role !== 'user') {
        return false;
      }

      const hasAssistantAfter = history.slice(messageIndex + 1).some((message) => message.role === 'assistant');
      if (hasAssistantAfter) {
        return false;
      }

      const assistantMessageId = generateMessageId('assistant');
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        toolInvocations: [],
        timestamp: new Date(),
        status: 'streaming',
      };

      assistantUsageReference.current.delete(assistantMessageId);
      assistantModelReference.current.set(assistantMessageId, null);

      setMessages((previous) => {
        const alreadyExists = previous.some((message) => message.id === assistantMessageId);
        return alreadyExists ? previous : [...previous, assistantMessage];
      });

      setIsLoading(true);
      setIsSyncing(true);
      updateStreamingState({ assistantId: assistantMessageId, started: false });
      latestAssistantIdReference.current = assistantMessageId;

      scheduleInitialScroll(scrollToBottom, 'smooth');

      const historyWithUser = history.slice(0, messageIndex + 1);

      try {
        log.info('Submitting chat (existing user message)', {
          messageId,
          textPreview: userMessage.content.slice(0, 80),
          textLength: userMessage.content.length,
          historyCount: historyWithUser.length,
          selectedModelId,
        });

        await runStream(assistantMessageId, {
          messages: buildMessagesPayload(historyWithUser),
          modelId: selectedModelId ?? undefined,
          agent: DEFAULT_AGENT_REQUEST,
        });
        return true;
      } catch (error: unknown) {
        const classification = errorHandlers.classifySubmissionError(error);
        if (classification.type === 'abort') {
          errorHandlers.applyAbortMessage(assistantMessageId);
          latestAssistantIdReference.current = null;
          assistantUsageReference.current.delete(assistantMessageId);
          assistantModelReference.current.delete(assistantMessageId);
          return true;
        }

        errorHandlers.applyFriendlyError(assistantMessageId, classification.friendlyMessage);
        if (onError) {
          onError(classification.friendlyMessage);
        }
        latestAssistantIdReference.current = null;
        assistantUsageReference.current.delete(assistantMessageId);
        assistantModelReference.current.delete(assistantMessageId);
        return false;
      } finally {
        setIsLoading(false);
        setIsSyncing(false);
        updateStreamingState({ assistantId: null, started: false });
        latestAssistantIdReference.current = null;
        abortControllerReference.current = null;
        assistantUsageReference.current.delete(assistantMessageId);
        assistantModelReference.current.delete(assistantMessageId);
        resetPendingAssistantText(assistantMessageId);
      }
    },
    [
      errorHandlers,
      generateMessageId,
      isLoading,
      log,
      messagesRef,
      onError,
      runStream,
      scrollToBottom,
      selectedModelId,
      setMessages,
      updateStreamingState,
      resetPendingAssistantText,
    ]
  );

  const handleSubmit = useCallback<SubmitFunction>(
    async (text: string) => {
      if (!text.trim() || isLoading) {
        return;
      }

      const userMessageId = generateMessageId('user');
      const userMessage: Message = {
        id: userMessageId,
        role: 'user',
        content: text,
        timestamp: new Date(),
      };

      const historyWithUser: Message[] = [...messagesRef.current, userMessage];

      const assistantMessageId = generateMessageId('assistant');
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        toolInvocations: [],
        timestamp: new Date(),
        status: 'streaming',
      };

      assistantUsageReference.current.delete(assistantMessageId);
      assistantModelReference.current.set(assistantMessageId, null);

      setMessages((previous) => [...previous, userMessage, assistantMessage]);

      clearInput();
      setIsLoading(true);
      setIsSyncing(true);
      updateStreamingState({ assistantId: assistantMessageId, started: false });
      latestAssistantIdReference.current = assistantMessageId;

      scheduleInitialScroll(scrollToBottom, 'smooth');

      try {
        log.info('Submitting chat', {
          textPreview: text.slice(0, 80),
          textLength: text.length,
          historyCount: historyWithUser.length,
          selectedModelId,
        });

        await runStream(assistantMessageId, {
          messages: buildMessagesPayload(historyWithUser),
          modelId: selectedModelId ?? undefined,
          agent: DEFAULT_AGENT_REQUEST,
        });
      } catch (error: unknown) {
        const classification = errorHandlers.classifySubmissionError(error);
        if (classification.type === 'abort') {
          errorHandlers.applyAbortMessage(assistantMessageId);
        } else {
          errorHandlers.applyFriendlyError(assistantMessageId, classification.friendlyMessage);
          if (onError) {
            onError(classification.friendlyMessage);
          }
        }
        latestAssistantIdReference.current = null;
        assistantUsageReference.current.delete(assistantMessageId);
        assistantModelReference.current.delete(assistantMessageId);
      } finally {
        setIsLoading(false);
        setIsSyncing(false);
        updateStreamingState({ assistantId: null, started: false });
        latestAssistantIdReference.current = null;
        abortControllerReference.current = null;
        assistantUsageReference.current.delete(assistantMessageId);
        assistantModelReference.current.delete(assistantMessageId);
        resetPendingAssistantText(assistantMessageId);
      }
    },
    [
      clearInput,
      errorHandlers,
      generateMessageId,
      isLoading,
      log,
      messagesRef,
      onError,
      runStream,
      scrollToBottom,
      selectedModelId,
      setMessages,
      updateStreamingState,
      resetPendingAssistantText,
    ]
  );

  const handleStop = useCallback(() => {
    if (!abortControllerReference.current) {
      return;
    }
    abortControllerReference.current.abort();
    setIsLoading(false);
    setIsSyncing(false);
    const assistantId = latestAssistantIdReference.current;
    if (assistantId === undefined || assistantId === null) {
      updateStreamingState({ assistantId: null, started: false });
      return;
    }
    errorHandlers.applyAbortMessage(assistantId);
    latestAssistantIdReference.current = null;
    assistantUsageReference.current.delete(assistantId);
    assistantModelReference.current.delete(assistantId);
    updateStreamingState({ assistantId: null, started: false });
    resetPendingAssistantText(assistantId);
  }, [errorHandlers, resetPendingAssistantText, updateStreamingState]);

  return {
    isLoading,
    isSyncing,
    streamingState,
    latestUIEvent,
    handleSubmit,
    handleStop,
    submitUserMessage,
  } satisfies UseChatStreamingResult;
};
